import type { SearchValues } from "@/types/flights";
import { initial, size, uniqueId } from "lodash";
import { X } from "lucide-react";
import { Dispatch, SetStateAction, useState } from "react";
import Button from "../../Button/Button";
import { Counter } from "./Counter";
import FlightClassDropdown from "./FlightClassDropdown";
import { useTranslations } from "next-intl";

type Props = {
  isFullScreenModal: boolean;
  onClose: () => void;
  values: SearchValues;
  setValues: Dispatch<SetStateAction<SearchValues>>;
};

type TemporaryValues = Pick<
  SearchValues,
  "adults" | "children" | "infants" | "flightClass"
>;

export const TravellersDropdownWithCabinClass = ({
  isFullScreenModal,
  values,
  onClose,
  setValues,
}: Props) => {
  const [flightClass, setFlightClass] = useState(values.flightClass);
  const [temporaryValues, setTemporaryValues] = useState<TemporaryValues>({
    adults: values.adults,
    children: values.children,
    infants: values.infants,
    flightClass: values.flightClass,
  });
  const t = useTranslations();

  const onDecrement = (key: "adults" | "children" | "infants", min = 0) => {
    if (isFullScreenModal) {
      setTemporaryValues((prev) => ({
        ...prev,
        [key]: size(prev[key]) === min ? prev[key] : initial(prev[key]),
      }));
      return;
    }

    setValues((prev) => ({
      ...prev,
      [key]: size(prev[key]) === min ? prev[key] : initial(prev[key]),
    }));
  };

  const onIncrement = (key: "adults" | "children" | "infants") => {
    if (isMaxReached) return;

    if (isFullScreenModal) {
      setTemporaryValues((prev) => ({
        ...prev,
        [key]: [...prev[key], { age: 4, id: uniqueId() }],
      }));
      return;
    }

    setValues((prev) => ({
      ...prev,
      [key]: [...prev[key], { age: 4, id: uniqueId() }],
    }));
  };

  const onAgeChange = (
    key: "adults" | "children" | "infants",
    age: number,
    travellerId: string
  ) => {
    if (isFullScreenModal) {
      setTemporaryValues((prev) => ({
        ...prev,
        [key]: prev[key].map((traveller) =>
          traveller.id === travellerId ? { ...traveller, age } : traveller
        ),
      }));
      return;
    }

    setValues((prev) => ({
      ...prev,
      [key]: prev[key].map((traveller) =>
        traveller.id === travellerId ? { ...traveller, age } : traveller
      ),
    }));
  };

  const onConfirm = () => {
    setValues((prev) => ({ ...prev, ...temporaryValues }));
    onClose();
  };

  const totalTravellers = (
    isFullScreenModal
      ? [
          ...temporaryValues.adults,
          ...temporaryValues.children,
          ...temporaryValues.infants,
        ]
      : [...values.adults, ...values.children, ...values.infants]
  ).length;

  const isMaxReached = totalTravellers >= 10;

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 z-20 bg-lightwhite dark:bg-sleap-gray-950 dark:text-white md:rounded-2xl md:absolute md:top-full md:right-auto md:bottom-auto md:p-2 md:mt-2 md:min-w-56 md:shadow-[0_23px_45px_-10px_rgba(0,0,0,0.25)] dark:border-sleap-gray-700 dark:border">
      {isFullScreenModal && (
        <div className="py-5 px-4 flex justify-between gap-2 items-center">
          <h3 className="font-inter text-lg font-medium">
            {" "}
            {t("Other.travellers")}
          </h3>
          <button
            type="button"
            className="p-2 bg-sleap-gray-200 dark:bg-sleap-gray-600"
            onClick={onClose}
          >
            <X />
          </button>
        </div>
      )}
      <ul>
        <li>
          <Counter
            ageOptions={[]}
            label={t("Other.adults")}
            description={t("HomePageFlights.16plusYo")}
            onDecrement={() => onDecrement("adults", 1)}
            onIncrement={() => onIncrement("adults")}
            disabledIncrement={isMaxReached}
            value={isFullScreenModal ? temporaryValues.adults : values.adults}
            min={1}
            onAgeChange={(age, travellerId) =>
              onAgeChange("adults", age, travellerId)
            }
          />
        </li>
        <li>
          <Counter
            label={t("HomePageFlights.children")}
            description={t("HomePageFlights.4to6yo")}
            onDecrement={() => onDecrement("children")}
            onIncrement={() => onIncrement("children")}
            disabledIncrement={isMaxReached}
            value={
              isFullScreenModal ? temporaryValues.children : values.children
            }
            onAgeChange={(age, travellerId) =>
              onAgeChange("children", age, travellerId)
            }
            ageOptions={[4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16]}
          />
        </li>
        <li>
          <Counter
            label={t("HomePageFlights.infants")}
            description={t("HomePageFlights.less4yo")}
            onDecrement={() => onDecrement("infants")}
            onIncrement={() => onIncrement("infants")}
            disabledIncrement={isMaxReached}
            ageOptions={[0, 1, 2, 3]}
            value={isFullScreenModal ? temporaryValues.infants : values.infants}
            onAgeChange={(age, travellerId) =>
              onAgeChange("infants", age, travellerId)
            }
          />
        </li>
      </ul>
      {isFullScreenModal && (
        <div className="py-5 px-4 mt-2">
          <div className="flex justify-between gap-2 items-center mb-4">
            <h3 className="font-inter text-lg font-medium">
              {" "}
              {t("HomePageFlights.cabinClass")}
            </h3>
          </div>
          <FlightClassDropdown
            value={flightClass}
            onChange={(value) => setFlightClass(value)}
          />
        </div>
      )}
      {isFullScreenModal && (
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-lightwhite shadow-[0_-20px_25px_-5px_rgba(0,0,0,0.1)] dark:bg-sleap-gray-850">
          <Button fullWidth onClick={onConfirm}>
            {t("Other.confirm")}
          </Button>
        </div>
      )}
    </div>
  );
};
